import { useMemo, useRef, useState } from 'react';

import classNames from 'classnames';

import { OrbitIcons } from '../constants.';

import { Icon } from './Icon';
import Search from './Search';

interface DualListBoxProps {
  className?: string;
  fieldId?: string;
  fieldLabel?: string;
  options?: any[];
  selected?: any[];
  onChange?: (selected: any[]) => void;
  onSearch?: (query: string) => void;
}

export const DualListBox = ({
  className,
  options = [],
  selected = [],
  fieldId = 'id',
  fieldLabel = 'name',
  onChange = () => {},
  onSearch = () => {},
}: DualListBoxProps): JSX.Element => {
  const [query, setQuery] = useState('');
  const leftRef = useRef<HTMLSelectElement>(null);
  const rightRef = useRef<HTMLSelectElement>(null);

  const filteredSelected = useMemo(() => {
    return selected.filter((s) => s[fieldLabel]?.toLowerCase()?.includes(query?.toLowerCase()));
  }, [selected, query, fieldLabel]);

  const filteredOptions = useMemo(() => {
    return options.filter((opt) => !selected.find((s) => s[fieldId] === opt[fieldId]));
  }, [selected, options, fieldId]);

  const handleAdd = (): void => {
    if (leftRef.current?.selectedOptions) {
      const values = Array.from(leftRef.current?.selectedOptions, (option) => option.value);
      const newSelected = options.filter((opt: any) => values.includes(opt[fieldId]));
      onChange([...selected, ...newSelected]);
    }
  };

  const handleAddAll = (): void => {
    onChange([...filteredOptions]);
  };

  const handleRemoveAll = (): void => {
    onChange([]);
  };

  const handleRemove = (): void => {
    if (rightRef.current?.selectedOptions) {
      const values = Array.from(rightRef.current?.selectedOptions, (option) => option.value);
      const newSelected = selected.filter((opt: any) => !values.includes(opt[fieldId]));
      onChange([...newSelected]);
    }
  };

  const handleSearch = (value: string): void => {
    setQuery(value);
  };

  return (
    <div className={classNames('flex overflow-hidden max-w-full', className)}>
      <div className="flex flex-col" style={{ width: '45%' }}>
        <Search
          className="sm:w-full mb-2"
          placeholder="Search..."
          setSearch={onSearch}
          changeSearchParams={false}
        />
        <select
          multiple
          className="border border-gray-1.0 rounded-md h-full overflow-auto w-full"
          ref={leftRef}
        >
          {filteredOptions.map((opt) => (
            <option className="p-1" key={opt[fieldId]} value={opt[fieldId]}>
              {opt[fieldLabel]}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center justify-center flex-1 px-2">
        <div className="flex flex-col items-center justify-center">
          <button className="mb-2" onClick={handleAdd} type="button">
            <Icon name={OrbitIcons.ChevronRight} size={24} />
          </button>
          <button className="mb-2" onClick={handleRemove} type="button">
            <Icon name={OrbitIcons.ChevronLeft} size={24} />
          </button>
          <button className="mb-2" onClick={handleAddAll} type="button">
            <Icon name={OrbitIcons.ChevronsRight} size={24} />
          </button>
          <button className="mb-2" onClick={handleRemoveAll} type="button">
            <Icon name={OrbitIcons.ChevronsLeft} size={24} />
          </button>
        </div>
      </div>
      <div className="flex flex-col" style={{ width: '45%' }}>
        <Search
          className="sm:w-full mb-2"
          placeholder="Search..."
          setSearch={handleSearch}
          changeSearchParams={false}
        />
        <select
          multiple
          className="border border-gray-1.0 rounded-md h-full overflow-auto w-full"
          ref={rightRef}
        >
          {filteredSelected.map((opt) => (
            <option className="p-1" key={opt[fieldId]} value={opt[fieldId]}>
              {opt[fieldLabel]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
